import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';

import { genConfig } from 'funifier-nice-avatar';

import { useAuth } from 'context/AuthContext';
import { useModal } from 'context/ModalContext';

import { ComportamentoHelpModal } from 'components/comportamento-help-modal';
import { GroupDetailModal } from 'components/group-detail-modal';
import { SeloMoedasModal } from 'components/moeda-help-modal';
import ModalFeedbackSinalizacao from 'components/modal-feedback-sinalizacao';
import NewFeatures from 'components/new-features';
import Notification from 'components/notification';
import { PontosHelpModal } from 'components/pontos-help-modal';
import Quiz from 'components/quiz';
import { RebrandShell, useRebrandShell } from 'components/rebrand/RebrandShell';
import { displayFirstName } from 'components/rebrand/mappings';
import UserTutorial from 'components/user-tutorial';
import { NotAllowed } from 'pages/NotAllowed';
import { buscarFeedbackPendente, FeedbackPendente } from 'services/report.service';

import { DimensionPanel } from './components/dimension-panel';
import { RecentRecognizedPanel } from './components/recent-recognized';
import { RecognitionHighlight } from './components/recognition-highlight';

import './styles.scss';

const defautConfig = genConfig();

/** Conteúdo da Home — dentro do RebrandShell, que já expõe grupo/nível/equipe. */
function DashboardContent() {
  const { user, userInfo, refreshUser, currentCycle } = useAuth();
  const { showModal } = useModal();
  const { groupInfo } = useRebrandShell();

  const [avatarConfig, setAvatarConfig] = useState(defautConfig);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [openTutorial, setOpenTutorial] = useState(false);
  const [closeFeatures, setCloseFeatures] = useState(false);
  const [feedbacksPendentes, setFeedbacksPendentes] = useState<FeedbackPendente[]>([]);

  const location = useLocation();
  const navigate = useNavigate();

  const handleAvatarClick = useCallback(() => {
    showModal('avatar', { config: avatarConfig });
  }, [avatarConfig, showModal]);

  useEffect(() => {
    try {
      const locationState: any = location.state;
      if (locationState?.direction === 'back') {
        refreshUser();
      }
    } catch (e) {
      console.log(e);
    }
  }, [location, refreshUser]);

  useEffect(() => {
    function updateAvatar() {
      if (user?.extra.avatar) {
        setAvatarConfig(user.extra.avatar);
      }
    }

    function shouldShowTutorial() {
      if ((user?.extra.accept && !user?.extra.tutorial) || !localStorage.getItem('tutorial')) {
        setShowTutorial(true);
      }
    }

    if (!user || !currentCycle) return;

    updateAvatar();
    shouldShowTutorial();

    const returnURL = sessionStorage.getItem('return');
    if (returnURL) {
      sessionStorage.removeItem('return');
      navigate(returnURL);
    }
  }, [user, currentCycle, navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const updateAvatar = searchParams.get('avatar');
    const openQuiz = searchParams.get('quiz');
    const openTutorialParam = searchParams.get('tutorial');

    if (updateAvatar) {
      setTimeout(() => handleAvatarClick(), 1e2);
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (openQuiz) {
      setShowQuiz(true);
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (openTutorialParam) {
      setShowTutorial(true);
      setOpenTutorial(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    async function carregarFeedback() {
      const pendentes = await buscarFeedbackPendente();
      setFeedbacksPendentes(pendentes);
    }

    /* Só com jogador carregado: o agregado usa o contexto do token, e
       chamar antes teria o mesmo efeito de chamar deslogado. */
    if (user) {
      carregarFeedback();
    }
  }, [user]);

  return (
    <>
      <RecognitionHighlight />

      <RecentRecognizedPanel />

      <DimensionPanel />

      {showTutorial && closeFeatures && (
        <UserTutorial
          open={openTutorial}
          onClose={() => {
            setOpenTutorial(false);
            setShowTutorial(false);
          }}
        />
      )}
      {userInfo && <NewFeatures player={userInfo} send={() => setCloseFeatures(true)} />}
      <ComportamentoHelpModal />
      <SeloMoedasModal />
      <PontosHelpModal />
      <GroupDetailModal group={groupInfo} />
      <Notification />
      {showQuiz && <Quiz onClose={() => setShowQuiz(false)} setQuizAnswered={() => {}} />}
      {feedbacksPendentes.length > 0 && (
        <ModalFeedbackSinalizacao
          key={feedbacksPendentes[0]._id}
          feedback={feedbacksPendentes[0]}
          onConfirmado={() => setFeedbacksPendentes(atual => atual.slice(1))}
        />
      )}
    </>
  );
}

export function Dashboard() {
  const { user, userInfo } = useAuth();

  if (!user) return null;

  if (!user.extra.id) {
    return <NotAllowed />;
  }


  return (
    <>
      <Outlet />

      <RebrandShell
        title="Painel de Reconhecimento"
        subtitle={`Bem-vindo de volta, ${displayFirstName(userInfo, user)}.`}
      >
        <DashboardContent />
      </RebrandShell>
    </>
  );
}
